import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import Footer from "@/components/Footer";
import { usePageMeta } from "@/seo";
import { trackCall, trackText, trackBook } from "@/analytics";

export default function BookOnline() {
  usePageMeta("/book-online");
  useEffect(() => {
    function handleIFrameMessage(e) {
      var clinikoBookings = document.getElementById('cliniko-19562517');
      if (typeof e.data !== 'string') return;
      if (e.data.search('cliniko-bookings-resize') > -1) {
        var height = Number(e.data.split(':')[1]);
        if (clinikoBookings && height > 0) {
          clinikoBookings.style.height = Math.max(height + 24, 420) + 'px';
        }
      }
      if (e.data.search('cliniko-bookings-page') > -1 && clinikoBookings) {
        clinikoBookings.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
    }

    window.addEventListener('message', handleIFrameMessage);
    
    return () => {
      window.removeEventListener('message', handleIFrameMessage);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#F7F5F0]" data-testid="book-online-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-[#E4DCCF]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="font-heading text-xl text-[#1A221E] tracking-tight">
            Dr. Greenspan
          </Link>
          <nav className="hidden sm:flex items-center gap-8">
            <Link to="/#about" className="font-body text-sm text-[#5C6A61] hover:text-[#1A221E] transition-colors">About</Link>
            <Link to="/#services" className="font-body text-sm text-[#5C6A61] hover:text-[#1A221E] transition-colors">Services</Link>
            <Link to="/blog" className="font-body text-sm text-[#5C6A61] hover:text-[#1A221E] transition-colors">Blog</Link>
            <Link to="/#contact" className="font-body text-sm text-[#5C6A61] hover:text-[#1A221E] transition-colors">Contact</Link>
            <Link 
              to="/book-online" 
              className="font-body text-sm font-medium text-white bg-[#C87B5D] hover:bg-[#b56a4e] px-5 py-2 rounded-full transition-colors"
            >
              Book Online
            </Link>
          </nav>
        </div>
      </header>

      {/* Page Content */}
      <section className="py-16 sm:py-20 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <p className="font-body text-xs tracking-[0.2em] uppercase font-bold text-[#C87B5D] mb-4">
              Schedule Now
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl font-medium text-[#1A221E] tracking-tighter leading-none mb-4">
              Book Online
            </h1>
            <p className="font-body text-base text-[#5C6A61] max-w-lg mx-auto">
              Select a service and time that works for you. If you don't see a time that fits,{" "}
              <a href="tel:+18286913551" onClick={trackCall("book_online")} className="text-[#C87B5D] font-medium hover:underline" data-testid="book-call-link">call</a> or{" "}
              <a href="sms:+18286913551" onClick={trackText("book_online")} className="text-[#C87B5D] font-medium hover:underline" data-testid="book-text-link">text (828) 691-3551</a>.
            </p>
          </div>

          {/* Cliniko Booking Widget */}
          <div className="bg-white rounded-3xl border border-[#E4DCCF] p-2 sm:p-6 shadow-sm" data-testid="cliniko-widget-container">
            <iframe 
              id='cliniko-19562517' 
              src='https://dr-kevin-greenspan.ca1.cliniko.com/bookings?embedded=true' 
              frameBorder='0' 
              scrolling='auto' 
              width='100%' 
              height='600' 
              loading="lazy"
              style={{ width: '100%', border: 0, display: 'block', pointerEvents: 'auto', transition: 'height 0.25s ease' }} 
              allow='payment'
              title="Book an appointment with Dr. Kevin Greenspan"
              data-testid="cliniko-iframe"
            />
          </div>

          <div className="text-center mt-8">
            <p className="font-body text-sm text-[#5C6A61] mb-3">Booking calendar not loading?</p>
            <a
              href="https://dr-kevin-greenspan.ca1.cliniko.com/bookings"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-[#3A4F41] hover:bg-[#2d3e33] text-white font-body text-sm font-medium px-6 py-3 transition-colors"
              onClick={trackBook}
              data-testid="book-fallback-link"
            >
              Book an appointment
              <ExternalLink size={14} />
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
